<template>
    <main-layout>
        <div slot="header" class="home-head">
            <div>
                <div class="home-date">{{ today }}</div>
                <h2>{{ greeting }}{{ firstName ? ', ' + firstName : '' }}</h2>
            </div>
            <div class="home-actions">
                <router-link class="el-button el-button--default el-button--small" to="/buildium/lease-rents">Create rent increases</router-link>
                <router-link class="el-button el-button--default el-button--small" to="/buildium/rental-units">End of tenancy notice</router-link>
                <router-link class="el-button el-button--default el-button--small" to="/inspection-reports">Inspection reports</router-link>
            </div>
        </div>

        <section class="home-section">
            <h3>Needs your attention</h3>
            <div class="home-cards" v-loading="loading.today">
                <router-link v-for="card in cards" :key="card.label" :to="card.to" class="home-card">
                    <span class="home-card-label">{{ card.label }}</span>
                    <span class="home-card-num">{{ card.value === null ? '—' : card.value }}</span>
                    <span class="home-card-note" :class="{warn: card.warn}">{{ card.note }}</span>
                </router-link>
            </div>
        </section>

        <section class="home-section" v-if="isAdmin && runChecks.length">
            <h3>Monthly rent increase run</h3>
            <div class="home-panel">
                <p class="home-muted">Drafts are created on the 15th of each month for increases taking effect about four months later. Each property manager confirms their own.</p>
                <ul class="home-checks">
                    <li v-for="c in runChecks" :key="c.label" :class="c.ok ? 'ok' : 'todo'">
                        <i class="bi" :class="c.ok ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'" aria-hidden="true"></i>
                        <span>{{ c.label }}</span>
                        <router-link v-if="!c.ok" to="/rent-increase">Set up</router-link>
                    </li>
                </ul>
            </div>
        </section>

        <div class="home-two">
            <section class="home-section">
                <h3>Recent activity</h3>
                <div class="home-panel home-recent" v-loading="loading.home">
                    <router-link v-for="a in activity" :key="a.id" class="home-row"
                                 :to="{path: '/forms', query: {type: a.form_type, open: String(a.id)}}">
                        <span class="home-row-time home-muted">{{ when(a.at) }}</span>
                        <span class="home-row-addr">
                            <b>{{ a.user || 'Someone' }}</b> {{ a.what }} {{ typeLabel(a.form_type).toLowerCase() }} ·
                            {{ a.unit_address || ('Form #' + a.id) }}
                        </span>
                    </router-link>
                    <p v-if="!loading.home && !activity.length" class="home-muted">Nothing yet.</p>
                </div>
            </section>
            <section class="home-section" v-if="isAdmin && freshness.length">
                <h3>Data from Buildium</h3>
                <div class="home-panel">
                    <div v-for="f in freshness" :key="f.label" class="home-fresh">
                        <span>{{ f.label }}</span>
                        <span class="home-muted">{{ f.at ? 'last change ' + ago(f.at) : 'no data' }}</span>
                    </div>
                    <p class="home-muted home-note">Bills arrive from Buildium as they change (webhook).</p>
                </div>
            </section>
        </div>
    </main-layout>
</template>

<script>
import {isAdministrator, visibleNav} from "../layout/navigation";

const TYPE_LABELS = {
    rtb7: 'Rent increase',
    rtb30: 'End of tenancy',
    'inspection-report': 'Inspection',
};

export default {
    name: "Home",
    data() {
        return {
            todo: null,
            config: null,
            activity: [],
            freshness: [],
            bills: null,
            loading: {today: true, home: true},
        };
    },
    computed: {
        user() {
            return this.$store.getters.userInfo || {};
        },
        isAdmin() {
            return isAdministrator(this.user);
        },
        firstName() {
            return String(this.user.nickname || '').split(' ')[0];
        },
        greeting() {
            const h = new Date().getHours();
            return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
        },
        today() {
            return new Date().toLocaleDateString('en-CA', {weekday: 'long', month: 'long', day: 'numeric'});
        },
        canSeeBills() {
            return visibleNav(this.user).some(g => g.items.some(i => i.to === '/buildium/buildium-bills'));
        },
        cards() {
            const t = this.todo;
            const n = key => (t && t[key] ? t[key].total : null);
            const bills = this.canSeeBills ? [{
                label: 'Bills to publish', value: this.bills ? this.bills.to_publish : null, to: '/buildium/buildium-bills',
                note: this.bills && this.bills.missing_files ? `${this.bills.missing_files} missing their files` : 'From Dext, not yet in Buildium',
                warn: !!(this.bills && (this.bills.to_publish || this.bills.missing_files)),
            }] : [];
            return [
                {label: 'Rent increases to confirm', value: n('confirm'), to: '/forms?type=rtb7&confirmation=pending&mine=1',
                    note: 'Assigned to you', warn: n('confirm') > 0},
                {label: 'Notices to serve', value: n('serve'), to: '/forms?type=rtb7&mine=1&served=0',
                    note: 'Confirmed, no proof of service yet'},
                {label: 'Unfinished inspection reports', value: n('drafts'), to: '/inspection-reports',
                    note: 'Your drafts'},
            ].concat(bills);
        },
        // What the next monthly run needs, for administrators.
        runChecks() {
            const c = this.config;
            if (!c) return [];
            const year = new Date().getFullYear() + 1;
            const rate = (c.rates || {})[year];
            return [
                {label: rate && rate.confirmed_at ? `${year} rate ${rate.rate}% confirmed` : `${year} rate not confirmed — no drafts will be created`, ok: !!(rate && rate.confirmed_at)},
                {label: c.default_admin ? `Default administrator: ${c.default_admin.nickname || c.default_admin.email}` : 'No default administrator for properties without a rental manager', ok: !!c.default_admin},
            ];
        },
    },
    methods: {
        typeLabel(t) {
            return TYPE_LABELS[t] || t;
        },
        when(iso) {
            const d = new Date(iso);
            const today = new Date();
            return d.toDateString() === today.toDateString()
                ? d.toLocaleTimeString('en-CA', {hour: 'numeric', minute: '2-digit'})
                : d.toLocaleDateString('en-CA', {month: 'short', day: 'numeric'});
        },
        ago(iso) {
            const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
            if (mins < 60) return mins <= 1 ? 'just now' : `${mins} min ago`;
            const hours = Math.round(mins / 60);
            if (hours < 48) return `${hours} h ago`;
            return new Date(iso).toLocaleDateString('en-CA', {month: 'short', day: 'numeric'});
        },
    },
    mounted() {
        this.$api.get('/sterling/today').then(r => {
            this.todo = r.data;
        }).catch(() => {
            this.todo = null;
        }).finally(() => {
            this.loading.today = false;
        });
        this.$api.get('/sterling/home').then(r => {
            this.activity = r.data.activity || [];
            this.freshness = r.data.freshness || [];
            this.bills = r.data.bills || null;
        }).catch(() => {
            this.activity = [];
        }).finally(() => {
            this.loading.home = false;
        });
    },
    // userInfo arrives after the page mounts when it is opened directly (refresh, link): fetch once it says admin.
    watch: {
        isAdmin: {
            immediate: true,
            handler(admin) {
                if (!admin || this.configRequested) return;
                this.configRequested = true;
                this.$api.get('/sterling/rent-increase/config').then(r => {
                    this.config = r.data;
                }).catch(() => {
                    this.config = null;
                });
            },
        },
    },
}
</script>

<style scoped>
.home-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
}

.home-head h2 {
    font-size: 22px;
    font-weight: 700;
}

.home-date, .home-muted {
    color: #5B6478;
    font-size: 13px;
}

.home-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.home-section {
    margin: 16px 6px 0;
}

.home-section h3 {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 10px;
}

.home-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
}

.home-two {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 0 8px;
}

.home-fresh {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 0;
    border-top: 1px solid #EEF0F5;
    font-size: 14px;
}

.home-fresh:first-child {
    border-top: 0;
}

.home-note {
    margin: 8px 0 0;
}

@media (max-width: 1000px) {
    .home-two {
        grid-template-columns: minmax(0, 1fr);
    }

    .home-row {
        grid-template-columns: 70px minmax(0, 1fr) !important;
    }
}

.home-card, .home-panel {
    background: #FFFFFF;
    border: 1px solid #E3E6EE;
    border-radius: 12px;
}

.home-card {
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: inherit;
}

.home-card:hover {
    border-color: #1F2D5D;
}

.home-card-label {
    color: #5B6478;
    font-size: 13px;
}

.home-card-num {
    font-size: 30px;
    font-weight: 700;
    color: #1F2D5D;
}

.home-card-note {
    color: #5B6478;
    font-size: 13px;
}

.home-card-note.warn {
    color: #92400E;
}

.home-panel {
    padding: 16px 18px;
}

.home-checks {
    list-style: none;
    margin: 10px 0 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.home-checks li {
    display: flex;
    align-items: center;
    gap: 8px;
}

.home-checks li.ok i {
    color: #2F9E44;
}

.home-checks li.todo i {
    color: #B45309;
}

.home-recent {
    padding: 4px 0;
}

.home-row {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr);
    gap: 12px;
    padding: 10px 18px;
    color: inherit;
    border-top: 1px solid #EEF0F5;
    align-items: center;
}

.home-row:first-child {
    border-top: 0;
}

.home-row:hover {
    background: #F7F8FB;
}

.home-row-type {
    font-weight: 600;
    color: #1F2D5D;
}

.home-row-addr {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.home-row-time {
    white-space: nowrap;
}
</style>
